import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Space, Typography } from 'antd';
import { ReloadOutlined, ArrowLeftOutlined, HomeOutlined, WifiOutlined } from '@ant-design/icons';
import i18n from '@/i18n';

const { Text } = Typography;

interface LazyErrorBoundaryProps {
  children: ReactNode;
  /** Maximum number of auto-retries for chunk loading failures */
  maxRetries?: number;
  /** Whether to enable auto-retry for chunk loading errors */
  retryable?: boolean;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface LazyErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
}

/**
 * Checks if an error is a chunk loading error (from code splitting)
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return (
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch') ||
    message.includes('dynamically imported module') ||
    message.includes('loading css chunk') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * LazyErrorBoundary - Specialized error boundary for lazy-loaded (code-split) components
 *
 * Features:
 * - Detects chunk loading failures (network issues, stale cache, app updates)
 * - Auto-retries with exponential backoff
 * - User-friendly recovery options
 * - Distinct UI for network vs application errors
 */
export class LazyErrorBoundary extends Component<LazyErrorBoundaryProps, LazyErrorBoundaryState> {
  static defaultProps = {
    maxRetries: 3,
    retryable: true,
  };

  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<LazyErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('LazyErrorBoundary caught error:', error);

    this.setState({ error, errorInfo });
    this.props.onError?.(error, errorInfo);

    const isChunkError = isChunkLoadError(error);

    if (isChunkError) {
      console.warn('Chunk loading failed - possible causes: network issue, stale cache, or app update');
    }

    // Auto-retry for chunk loading errors with exponential backoff
    const { maxRetries = 3, retryable = true } = this.props;
    if (isChunkError && retryable && this.state.retryCount < maxRetries) {
      const backoffMs = 1000 * Math.pow(2, this.state.retryCount); // 1s, 2s, 4s
      console.log(`Auto-retrying in ${backoffMs}ms (attempt ${this.state.retryCount + 1}/${maxRetries})...`);

      this.setState({ isRetrying: true });

      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prev.retryCount + 1,
          isRetrying: false,
        }));
      }, backoffMs);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
    });
  };

  handleReload = () => {
    // Clear module cache and reload
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    const { maxRetries = 3 } = this.props;
    const { hasError, error, retryCount, isRetrying } = this.state;
    const t = i18n.t.bind(i18n);

    // Show loading state during auto-retry
    if (isRetrying) {
      return (
        <div style={{
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}>
          <Result
            icon={<WifiOutlined style={{ color: '#1890ff' }} spin />}
            title={t('shared.lazyErrorBoundary.retrying')}
            subTitle={t('shared.lazyErrorBoundary.retryingSub', { current: retryCount + 1, total: maxRetries })}
          />
        </div>
      );
    }

    if (hasError && error) {
      const isChunkError = isChunkLoadError(error);
      const attemptsLeft = maxRetries - retryCount;

      return (
        <div style={{
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}>
          <Result
            status={isChunkError ? 'warning' : 'error'}
            title={isChunkError ? t('shared.lazyErrorBoundary.failedTitle') : t('shared.lazyErrorBoundary.errorTitle')}
            subTitle={
              isChunkError ? (
                <Space orientation="vertical" size="small">
                  <Text>{t('shared.lazyErrorBoundary.loadFailedMessage')}</Text>
                  <Text type="secondary">
                    {t('shared.lazyErrorBoundary.loadFailedSub')}
                    {attemptsLeft > 0 && t('shared.lazyErrorBoundary.retriesRemaining', { count: attemptsLeft })}
                  </Text>
                </Space>
              ) : (
                <Text>{t('shared.lazyErrorBoundary.errorMessage')}</Text>
              )
            }
            extra={
              <Space wrap>
                {attemptsLeft > 0 && (
                  <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleRetry}>
                    {t('shared.lazyErrorBoundary.retryLeft', { count: attemptsLeft })}
                  </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={this.handleReload}>
                  {t('shared.lazyErrorBoundary.reloadPage')}
                </Button>
                <Button icon={<ArrowLeftOutlined />} onClick={this.handleGoBack}>
                  {t('shared.lazyErrorBoundary.goBack')}
                </Button>
                <Button icon={<HomeOutlined />} onClick={this.handleGoHome}>
                  {t('shared.lazyErrorBoundary.goHome')}
                </Button>
              </Space>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default LazyErrorBoundary;
